import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calculator, Link as LinkIcon, Loader2, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { usePartnerSubscriptionLinks } from '@/hooks/usePartnerSettings';
import { supabase } from '@/lib/supabaseClient';

type PartnerPlan = {
  id: string;
  plan_identifier: string;
  name: string;
  description?: string;
  monthly_credits: number;
  price_monthly: number;
  price_one_time: number;
  one_time_bonus_credits: number;
  plan_type: string;
  is_active: boolean;
};

export default function CoBrandedSubscriptions() {
  const { data: subscriptionLinks, isLoading: plansLoading, error: plansError, refetch: refetchSubscriptionLinks } = usePartnerSubscriptionLinks();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [isGenerateLinkOpen, setIsGenerateLinkOpen] = useState(false);
  const [selectedPlanForLink, setSelectedPlanForLink] = useState<PartnerPlan | null>(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [isLinkLoading, setIsLinkLoading] = useState(false);
  const [ensured, setEnsured] = useState(false);

  // Eagerly ensure links on mount (batch) and refresh list
  useEffect(() => {
    (async () => {
      if (ensured) return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-management?action=ensure-subscription-links`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          }
        });
        await refetchSubscriptionLinks();
        setEnsured(true);
      } catch (e) {
        console.warn('Ensure links failed (non-blocking):', e);
      }
    })();
  }, [ensured, refetchSubscriptionLinks]);

  const getOrCreateLink = async (plan: PartnerPlan) => {
    setLoadingPlanId(plan.plan_identifier);
    try {
      const updatedData = await refetchSubscriptionLinks();
      const planWithLink = updatedData.data?.plans?.find((p: any) => p.plan_identifier === plan.plan_identifier);

      if (planWithLink?.stripe_checkout_link) {
        return planWithLink.stripe_checkout_link as string;
      } else {
        // Generate on demand
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-management?action=generate-permanent-link`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ plan_identifier: plan.plan_identifier })
        });
        if (response.ok) {
          const result = await response.json();
          const link = result.checkout_link || '';
          try { await refetchSubscriptionLinks(); } catch {}
          return link;
        } else {
          return '';
        }
      }
    } catch (error) {
      console.error('Generate link error:', error);
      return '';
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleOpenModal = async (plan: PartnerPlan) => {
    setSelectedPlanForLink(plan);
    setIsGenerateLinkOpen(true);
    setIsLinkLoading(true);
    const link = await getOrCreateLink(plan);
    setGeneratedLink(link);
    setIsLinkLoading(false);
  };

  if (plansLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px] text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading subscriptions...
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Subscriptions</CardTitle>
            <CardDescription>{plansError.message || 'Failed to load plans'}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const plans: PartnerPlan[] = (subscriptionLinks?.plans || []) as PartnerPlan[];

  // Group plans by type
  const subscriptionPlans = plans.filter(plan => plan.plan_type === 'subscription');
  const oneTimePlans = plans.filter(plan => plan.plan_type === 'payment');

  const renderPlanCard = (plan: PartnerPlan) => (
    <Card key={plan.plan_identifier} className="border rounded-lg h-full flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{plan.name}</CardTitle>
            <CardDescription>
              {plan.plan_type === 'payment' && plan.one_time_bonus_credits > 0
                ? `${plan.one_time_bonus_credits.toLocaleString()} credits`
                : plan.monthly_credits > 0
                ? `${plan.monthly_credits.toLocaleString()} credits/month`
                : plan.one_time_bonus_credits > 0
                ? `${plan.one_time_bonus_credits.toLocaleString()} bonus credits`
                : '0 credits/month'}
            </CardDescription>
          </div>
          <div className="text-right">
            {plan.plan_type === 'payment' && plan.price_one_time > 0 ? (
              <>
                <div className="text-xl font-semibold">${plan.price_one_time}</div>
                <div className="text-xs text-muted-foreground">one-time</div>
              </>
            ) : (
              <>
                <div className="text-xl font-semibold">${plan.price_monthly}</div>
                <div className="text-xs text-muted-foreground">per month</div>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 flex-1 flex flex-col">
        {plan.description && (
          <p className="text-sm text-muted-foreground">{plan.description}</p>
        )}
        <div className="pt-1 mt-auto flex justify-end">
          <Button variant="default" size="sm" onClick={() => handleOpenModal(plan)} disabled={loadingPlanId === plan.plan_identifier}>
            {loadingPlanId === plan.plan_identifier ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Fetching link...
              </>
            ) : (
              <>
                <LinkIcon className="h-4 w-4 mr-2" /> Link
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Subscriptions</h2>
          <p className="text-muted-foreground">Share Schedule Hero AI subscriptions with your clients</p>
        </div>
      </div>

      {plans.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10 text-muted-foreground">
            <Calculator className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>No plans available</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calculator className="h-5 w-5 mr-2" /> Available Plans
            </CardTitle>
            <CardDescription>Standard plans offered by Schedule Hero AI</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={oneTimePlans.length > 0 ? "onetime" : "monthly"} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="onetime" disabled={oneTimePlans.length === 0}>
                  One-Time Purchases {oneTimePlans.length > 0 && `(${oneTimePlans.length})`}
                </TabsTrigger>
                <TabsTrigger value="monthly" disabled={subscriptionPlans.length === 0}>
                  Monthly Subscriptions {subscriptionPlans.length > 0 && `(${subscriptionPlans.length})`}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="onetime" className="mt-6">
                {oneTimePlans.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {oneTimePlans.map(renderPlanCard)}
                  </div>
                ) : (
                  <div className="text-center py-10 text-muted-foreground">
                    <p>No one-time purchase plans available</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="monthly" className="mt-6">
                {subscriptionPlans.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {subscriptionPlans.map(renderPlanCard)}
                  </div>
                ) : (
                  <div className="text-center py-10 text-muted-foreground">
                    <p>No monthly subscription plans available</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Link Modal (matches CustomPricing style) */}
      <Dialog open={isGenerateLinkOpen} onOpenChange={(open) => {
        setIsGenerateLinkOpen(open);
        if (!open) {
          setGeneratedLink('');
          setSelectedPlanForLink(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <LinkIcon className="h-5 w-5 mr-2" />
              Subscription Link
            </DialogTitle>
            <DialogDescription>
              Subscription link for "{selectedPlanForLink?.name}" plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPlanForLink && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="text-sm space-y-1">
                  <div className="font-medium">{selectedPlanForLink.name}</div>
                  <div>
                    {selectedPlanForLink.plan_type === 'payment' && selectedPlanForLink.one_time_bonus_credits > 0
                      ? `${selectedPlanForLink.one_time_bonus_credits.toLocaleString()} credits`
                      : selectedPlanForLink.monthly_credits > 0
                      ? `${selectedPlanForLink.monthly_credits.toLocaleString()} credits/month`
                      : selectedPlanForLink.one_time_bonus_credits > 0
                      ? `${selectedPlanForLink.one_time_bonus_credits.toLocaleString()} bonus credits`
                      : '0 credits/month'}
                  </div>
                  <div>
                    {selectedPlanForLink.plan_type === 'payment' && selectedPlanForLink.price_one_time > 0
                      ? `$${selectedPlanForLink.price_one_time} one-time`
                      : `$${selectedPlanForLink.price_monthly}/month`}
                  </div>
                </div>
              </div>
            )}

            {isLinkLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span>Fetching subscription link...</span>
              </div>
            ) : generatedLink ? (
              <div className="space-y-3">
                <label className="text-sm">Partner Link</label>
                <div className="flex gap-2">
                  <Input readOnly value={generatedLink} className="text-xs font-mono" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(generatedLink); toast.success('Link copied'); } catch { toast.error('Copy failed'); }
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(generatedLink, '_blank')}>
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                Link not available. Try again in a moment.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


